import axios from 'axios';
import { API_URL } from '../utils/constants.js';
import {
  cryptoEnabled,
  encryptPayload,
  decryptPayload,
  APP_TOKEN,
} from './crypto.js';

// Render's free tier sleeps after inactivity; a cold start can take ~30–50s.
// Use a generous timeout + a short retry on cold-start-style failures so the
// first request after idle doesn't fail the UI.
const REQUEST_TIMEOUT_MS = 60000;
const MAX_RETRIES = 2;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

apiClient.interceptors.request.use(
  async (config) => {
    const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
    console.log(`[postcards/api] → ${config.method?.toUpperCase()} ${fullUrl}`, {
      body: config.data,
    });
    config.metadata = { startedAt: Date.now() };

    // Encrypt the request body + attach the app token. Guard with __encrypted
    // so a retry (which re-runs this interceptor) doesn't double-encrypt the
    // already-wrapped { p } envelope.
    if (cryptoEnabled()) {
      config.headers['x-app-token'] = APP_TOKEN;
      if (!config.__encrypted && config.data !== undefined && config.data !== null) {
        config.data = { p: await encryptPayload(config.data) };
        config.__encrypted = true;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  async (response) => {
    // Decrypt the response body before anything else consumes it.
    if (
      cryptoEnabled() &&
      response.data &&
      typeof response.data.p === 'string'
    ) {
      response.data = await decryptPayload(response.data.p);
    }

    const ms = response.config.metadata
      ? Date.now() - response.config.metadata.startedAt
      : null;
    console.log(
      `[postcards/api] ← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url} (${ms}ms)`,
      { data: response.data }
    );
    return response;
  },
  async (error) => {
    const cfgEarly = error.config || {};
    // Retry cold-start-style failures: timeouts, network errors (no response),
    // or 502/503/504. ONLY for idempotent GETs — never retry POST/PATCH/DELETE
    // (a timed-out search may have actually run and would double-charge Melissa).
    const method = (cfgEarly.method || 'get').toLowerCase();
    const status = error.response?.status;
    const isTimeout =
      error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
    const isColdStart =
      !error.response || status === 502 || status === 503 || status === 504;
    if (
      method === 'get' &&
      (isTimeout || isColdStart) &&
      (cfgEarly.__retryCount || 0) < MAX_RETRIES
    ) {
      cfgEarly.__retryCount = (cfgEarly.__retryCount || 0) + 1;
      const backoff = 1000 * cfgEarly.__retryCount;
      console.warn(
        `[postcards/api] retry ${cfgEarly.__retryCount}/${MAX_RETRIES} after ${
          isTimeout ? 'timeout' : `status ${status || 'network'}`
        } — ${cfgEarly.method?.toUpperCase()} ${cfgEarly.url} (waking backend?)`
      );
      await sleep(backoff);
      return apiClient(cfgEarly);
    }

    // Decrypt encrypted error bodies so the message can be read.
    if (
      cryptoEnabled() &&
      error.response?.data &&
      typeof error.response.data.p === 'string'
    ) {
      try {
        error.response.data = await decryptPayload(error.response.data.p);
      } catch {
        // leave as-is if it can't be decrypted
      }
    }

    const cfg = error.config || {};
    const ms = cfg.metadata ? Date.now() - cfg.metadata.startedAt : null;
    console.error(
      `[postcards/api] ✗ ${error.response?.status || 'NETWORK'} ${cfg.method?.toUpperCase() || ''} ${cfg.url || ''} (${ms}ms)`,
      {
        message: error.message,
        responseData: error.response?.data,
      }
    );
    const message =
      error.response?.data?.message || error.message || 'An error occurred';
    // Keep the machine-readable bits on the rejected Error so callers can react
    // to a specific failure (e.g. DUPLICATE_SEARCH_AREA is shown as a warning,
    // not as a red error) instead of matching on the message text.
    const normalized = new Error(message);
    normalized.status = error.response?.status || null;
    normalized.errorCode = error.response?.data?.errorCode || null;
    return Promise.reject(normalized);
  }
);

export default apiClient;
